import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Paper, Button, TextField,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography, Box, IconButton,
    InputAdornment, Tooltip, Skeleton, TableSortLabel
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Add as AddIcon,
    CloudUpload as BulkUploadIcon,
    SettingsBackupRestore as RestoreIcon,
    Link as LinkIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    ErrorOutline as ErrorOutlineIcon,
    HourglassEmpty as HourglassEmptyIcon,
    Assignment as ClipboardIcon
} from '@mui/icons-material';
import { useAuth } from '../Auth';
import { useSnackbar } from '../context/SnackbarContext';
import { formatForInput, formatDate, formatHumanReadableTimestamp } from '../utils/dateUtils';
import WWCCDialog from './dialogs/WWCCDialog';
import { differenceInDays, isPast, parseISO, isValid } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axiosInstance';

const STATUS_MAP = {
    VALID: { label: 'Valid', color: 'success', icon: <CheckCircleIcon sx={{ fontSize: '1rem' }} /> },
    EXPIRING_SOON: { label: 'Expiring Soon', color: 'warning', icon: <HourglassEmptyIcon sx={{ fontSize: '1rem' }} /> },
    EXPIRED: { label: 'Expired', color: 'error', variant: 'outlined', icon: <ErrorOutlineIcon sx={{ fontSize: '1rem' }} /> },
    INVALID: { label: 'Invalid', color: 'info', icon: <WarningIcon sx={{ fontSize: '1rem' }} /> },
    PENDING: { label: 'Pending', color: 'default', variant: 'outlined' },
    BANNED: { label: 'Banned', color: 'error', icon: <WarningIcon sx={{ fontSize: '1rem' }} /> }
};

const getStatusText = (record) => {
    let status = record.status || 'PENDING';
    const parsedExpiry = record.expiry_date ? new Date(record.expiry_date) : null;
    const today = new Date();

    if (status === 'VALID' && parsedExpiry) {
        const days = differenceInDays(parsedExpiry, today);
        if (isPast(parsedExpiry)) return 'EXPIRED';
        if (days <= 90 && days > 0) return 'EXPIRING SOON';
    }
    return status;
};

export default function WWCC() {
    const { user } = useAuth();
    const tenantId = user?.tenant_id || user?.tenant?.uuid;
    const [records, setRecords] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [open, setOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [hardDeleteConfirmOpen, setHardDeleteConfirmOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const showSnackbar = useSnackbar();
    const canEdit = user.role === 'admin' || user.role === 'superuser';
    const navigate = useNavigate();
    const location = useLocation();
    const [hasAutoOpened, setHasAutoOpened] = useState(false);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('surname');

    const fetchRecords = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/wwcc');

            const now = new Date();
            const processedData = res.data.map(r => ({
                ...r,
                isExpired: r.expiry_date && new Date(r.expiry_date) < now
            }));
            setRecords(processedData);

        } catch (err) {
            showSnackbar("Failed to load records", "error");
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 10);
        }
    }, [showSnackbar]);

    useEffect(() => {
        fetchRecords();
    }, [fetchRecords]);


    useEffect(() => {
        if (!loading && records.length > 0 && location.state?.openRecordId && !hasAutoOpened) {
            const targetRecord = records.find(r => r.id === location.state.openRecordId);
            if (targetRecord) {
                handleOpen(targetRecord);
                setHasAutoOpened(true);
            }
        }
    }, [loading, records, location.state, hasAutoOpened]);

    
    const handleOpen = (record = null) => {
        setSelectedRecord(record);
        setIsEditing(!!record);
        setOpen(true);
    };


    const handleSave = async (payload) => {
        try {
            if (isEditing) {
                // Axios automatically throws if status is not 2xx
                await api.put(`/wwcc/${payload.id}`, payload);
                showSnackbar("Record updated successfully!", "success");
            } else {
                await api.post('/wwcc', payload);
                showSnackbar("Record created successfully!", "success");
            }

            setOpen(false);
            fetchRecords();
        } catch (err) {
            // Log the real error so you can see it in the console
            console.error("Save Error:", err);

            // Extract the error message from the Axios error object
            // const serverMessage = err.response?.data?.error || err.response?.data?.message;
            // showSnackbar(serverMessage || "Network error or server failure.", "error");*/

            if (err.response?.status === 403) {
                // Merge "Limit Reached" with the detailed description
                const title = err.response.data.error;
                const detail = err.response.data.message;

                // We use a colon and a space to separate them in the snackbar
                showSnackbar(`${title}:\n${detail}`, 'warning');
            } else {
                const errorMsg = err.response?.data?.error || 'Failed to save record';
                showSnackbar(errorMsg, 'error');
            }
        }
    };

    const handleDeleteClick = (record) => {
        setSelectedRecord(record);
        setDeleteConfirmOpen(true);
    };

    const checkLimit = async () => {
        try {
            const response = await api.get('/wwcc/usage-status');
            if (response.data.isFull) {
                showSnackbar(
                    <span>
                        <strong>Limit Reached:</strong><br /><br />
                        You are currently using {response.data.currentCount} of {response.data.limit} records.
                        Please upgrade your plan to add more.
                    </span>,
                    'warning',
                    10000
                );
                return false; // Stop the action
            }
            return true; // Allow the action
        } catch (err) {
            showSnackbar("Error checking subscription status", "error");
            return false;
        }
    };


    const handleConfirmDelete = async () => {
        if (selectedRecord) {
            try {
                await api.put(`/wwcc/${selectedRecord.id}`, {
                    ...selectedRecord,
                    is_active: false
                });

                showSnackbar(`Deleted ${selectedRecord.first_given_name} ${selectedRecord.surname} successfully.`);
                fetchRecords();
            } catch (err) {
                showSnackbar('Failed to deactivate record', 'error');
            }
            setDeleteConfirmOpen(false);
            setSelectedRecord(null);
        }
    };

    const handleReactivate = async (record) => {
        try {
            await api.put(`/wwcc/${record.id}`, {
                ...record,
                is_active: true
            });

            // Axios doesn't use res.ok; if it didn't throw an error, it succeeded
            showSnackbar("Record reactivated", "success");
            fetchRecords();

        } catch (err) {
            showSnackbar("Failed to reactivate record", "error");
            console.error(err);
        }
    };

    const handleHardDelete = async () => {
        if (selectedRecord) {
            try {
                await api.delete(`/wwcc/${selectedRecord.id}`);
                showSnackbar(`Permanently deleted ${selectedRecord.first_given_name} ${selectedRecord.surname}.`, "success");
                fetchRecords();
            } catch (err) {
                showSnackbar('Failed to permanently delete record', 'error');
                console.error(err);
            }
            setHardDeleteConfirmOpen(false);
            setSelectedRecord(null);
        }
    };

    const sortedRecords = React.useMemo(() => {
        // 1. Filter
        let data = records.filter(r =>
            `${r.first_given_name} ${r.other_given_names || ''} ${r.surname} ${r.wwcc_number}`
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
        );

        // 2. Sort
        return data.sort((a, b) => {
            if (a.is_active && !b.is_active) return -1;
            if (!a.is_active && b.is_active) return 1;

            let valA, valB;

            // 1. Get primary values
            if (orderBy === 'verified_status') {
                valA = getStatusText(a);
                valB = getStatusText(b);
            } else if (orderBy === 'days_left') {
                const today = new Date();
                valA = a.expiry_date ? differenceInDays(parseISO(a.expiry_date), today) : null;
                valB = b.expiry_date ? differenceInDays(parseISO(b.expiry_date), today) : null;
            } else {
                // FIX: Map the UI column name ('state') to the object property ('au_state')
                const targetKey = orderBy === 'state' ? 'au_state' : orderBy;
                valA = a[targetKey];
                valB = b[targetKey];
            }

            // 2. Handle null/blank/undefined values to force them to the end
            const valAEmpty = valA === null || valA === undefined || valA === '';
            const valBEmpty = valB === null || valB === undefined || valB === '';

            if (valAEmpty && !valBEmpty) return 1;
            if (!valAEmpty && valBEmpty) return -1;
            if (valAEmpty && valBEmpty) return 0;

            // 3. Perform Primary Sort (only if neither is empty)
            if (valA !== valB) {
                // Optional enhancement: Use localeCompare for cleaner string handling (NSW vs VIC vs WA)
                if (typeof valA === 'string' && typeof valB === 'string') {
                    return (order === 'asc' ? 1 : -1) * valA.localeCompare(valB);
                }
                return (order === 'asc' ? 1 : -1) * (valA > valB ? 1 : -1);
            }

            // 4. Secondary Sort (Surname)
            return a.surname.localeCompare(b.surname);
        });
    }, [records, searchTerm, order, orderBy]);

    const handleCopyPublicLink = async () => {
        if (!tenantId) {
            showSnackbar("Error: Tenant information not found.", "error");
            return;
        }
        const tenantName = user?.tenant_name;
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + 90);  //90 days in the future
        const expiryStr = expiryDate.toISOString().split('T')[0];
        const compositeKey = btoa(`${tenantId}|${expiryStr}|${tenantName}`);
        const publicUrl = `${window.location.origin}/submit-wwcc/${compositeKey}`;
        /*
        Example:        
            http://localhost:4000/submit-wwcc/Yzc4ZTczNGYtZTYwMi00ZDJlLTljZjgtYmQ2Nzc0NWYxMzUxfDIwMjYtMDM=

        */
        try {
            await navigator.clipboard.writeText(publicUrl);
            showSnackbar(`Public submission link copied to clipboard!\nValid for 90 days.`, "success");
        } catch (err) {
            console.error("Failed to copy: ", err);
            // Fallback to execCommand if needed
            fallbackCopyTextToClipboard(publicUrl);
        }
    };

    function fallbackCopyTextToClipboard(text) {
        /*
        Example:
            http://localhost:4000/submit-wwcc/Yzc4ZTczNGYtZTYwMi00ZDJlLTljZjgtYmQ2Nzc0NWYxMzUxfDIwMjYtMDM=
        */
        const textArea = document.createElement("textarea");
        textArea.value = text;

        // Ensure the textarea is off-screen
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            const successful = document.execCommand('copy');
            if (successful) {
                showSnackbar("Link copied! (fallback)", "success");
            } else {
                showSnackbar("Failed to copy link.", "error");
            }
        } catch (err) {
            showSnackbar("Unable to copy.", "error");
        }

        document.body.removeChild(textArea);
    }

    const exportToCSV = (data) => {
        if (!data || data.length === 0) return;

        // 1. Define Headers (Matches your table columns)
        const headers = [
            "First Name",
            "Other Given Names",
            "Surname",
            "Birthdate",
            "State",
            "WWCC Number",
            "Expiry Date",
            "Verified Status"
        ];

        // 2. Map data to rows
        const rows = data.map(r => [
            `"${r.first_given_name}"`,
            `"${r.other_given_names}"`,
            `"${r.surname}"`,
            `"${formatForInput(r.birthdate)}"`,
            `"${r.au_state}"`,
            `"${r.wwcc_number}"`,
            `"${formatForInput(r.expiry_date)}"`,
            `"${getStatusText(r)}"`
        ]);

        // 3. Combine headers and rows
        const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");

        // 4. Create a Blob and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.setAttribute("href", url);
        link.setAttribute("download", `WWCC_Export_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };



    // Simplify this helper to be a pure component or just a simpler return
    const renderStatusChip = (record) => {
        let statusKey = record.status || 'PENDING';

        // 1. Parse Dates safely
        const expiryValue = record.expiry_date;
        const parsedExpiry = expiryValue ? new Date(expiryValue) : null;
        const today = new Date();

        const daysUntilExpiry = parsedExpiry ? differenceInDays(parsedExpiry, today) : null;
        const isPastDate = parsedExpiry ? isPast(parsedExpiry) : false;

        // --- LOGIC OVERRIDES ---
        // Only apply date-based overrides if the current status is 'VALID'
        if (statusKey === 'VALID') {
            if (expiryValue && isPastDate) {
                statusKey = 'EXPIRED';
            }
            else if (expiryValue && daysUntilExpiry !== null && daysUntilExpiry <= 90 && daysUntilExpiry > 0) {
                statusKey = 'EXPIRING_SOON';
            }
        }

        // 3. Get configuration from Map
        const config = STATUS_MAP[statusKey] || STATUS_MAP.PENDING;

        return (
            <Chip
                label={config.label}
                color={config.color}
                variant={config.variant || 'filled'}
                size="small"
                icon={config.icon}
                sx={{
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    height: 24,
                    '& .MuiChip-icon': {
                        color: 'inherit' // Ensures the icon takes the color of the text/label
                    }
                }}
            />
        );
    };

    return (
        <Box sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>

            {/* Header & Action Cards */}
            <Box sx={{ flexShrink: 0, mb: 2 }}>
                <Typography variant="h4" gutterBottom>Working With Children Checks</Typography>

                {/* Add buttons and Search */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between', // Pushes left-group and right-group apart
                    alignItems: 'center',
                    gap: 2
                }}>
                    {/* LEFT GROUP: Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        {canEdit && (
                            <>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    // onClick={() => handleOpen()} // Manual Add Dialog
                                    onClick={async () => {
                                        const canAdd = await checkLimit();
                                        if (canAdd) {
                                            // setSelectedRecord(null);
                                            // setDialogOpen(true);
                                            handleOpen()
                                        }
                                    }}
                                    sx={{ height: 40, textTransform: 'none' }}
                                >
                                    Add New Manual Record
                                </Button>

                                <Button
                                    variant="outlined"
                                    startIcon={<BulkUploadIcon />}
                                    // onClick={() => navigate('/settings/wwcc-import')}
                                    onClick={async (e) => {
                                        const canAdd = await checkLimit();
                                        // if (!canAdd) {
                                        //     e.preventDefault(); // Stop the file picker from opening
                                        // }
                                        if (canAdd) {
                                            navigate('/settings/wwcc-import')
                                        }
                                    }}
                                    sx={{ height: 40, textTransform: 'none' }}
                                >
                                    Upload records from CSV
                                </Button>
                            </>
                        )}
                    </Box>

                    {/* RIGHT GROUP: Search Field */}
                    <TextField
                        size="small"
                        placeholder="Search name or number..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }}
                        sx={{ width: 300 }}
                    />
                </Box>

            </Box>



            {/* --- WRAP BOTH HEADER AND TABLE IN THIS PAPER --- */}
            <Paper sx={{
                p: 0,
                boxShadow: 3,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                minHeight: 200,
                mx: 0
            }}>
                {/* The Header Box is now INSIDE the Paper */}
                <Box sx={{
                    p: 1.5,
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Current WWCC Records ({sortedRecords.length})
                    </Typography>

                    {/* This Box groups the buttons together on the right */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

                        {canEdit && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {/* NEW: PUBLIC LINK BUTTON */}
                                <Tooltip title="Copy external submission link to clipboard">
                                    <IconButton
                                        onClick={handleCopyPublicLink}
                                        sx={{
                                            color: 'white',
                                            transition: 'transform 0.2s',
                                            '&:hover': { transform: 'scale(1.1)', bgcolor: 'rgba(255,255,255,0.1)' }
                                        }}
                                    >
                                        <LinkIcon sx={{ fontSize: 24 }} />
                                    </IconButton>
                                </Tooltip>

                                {/* EXCEL EXPORT BUTTON */}
                                <Tooltip title="Export to CSV">
                                    <IconButton
                                        onClick={() => exportToCSV(sortedRecords)}
                                        sx={{
                                            p: 0,
                                            transition: 'transform 0.2s',
                                            '&:hover': { transform: 'scale(1.1)', bgcolor: 'rgba(255,255,255,0.1)' }
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src="/Microsoft_Office_Excel_Logo_64px.png"
                                            alt="Export Excel"
                                            sx={{ width: 28, height: 28 }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}

                    </Box>

                </Box>

                {/* The TableContainer no longer needs to be a 'Paper' itself */}
                <TableContainer sx={{
                    flexGrow: 1,
                    overflowX: 'auto', // Explicitly allow horizontal scroll
                    overflowY: 'auto',
                    width: '100%'      // Ensure it takes the full width of the Paper
                }}>
                    <Table
                        stickyHeader
                        sx={{
                            minWidth: 'max-content', // 2. Force the table to be as wide as its content
                            '& .MuiTableCell-root': {
                                py: 0.8,            // Reduces vertical padding for tighter rows
                                lineHeight: '1.3',
                                whiteSpace: 'nowrap', // 3. Prevent text from wrapping to a new line
                                px: 2                 // Add a little extra horizontal padding for breathability
                            }
                        }}
                    >
                        <TableHead sx={{ bgcolor: 'grey.100' }}>
                            <TableRow>
                                {/* <TableCell><strong>Surname</strong></TableCell> */}
                                <TableCell sortDirection={orderBy === 'surname' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'surname'}
                                        direction={orderBy === 'surname' ? order : 'asc'}
                                        onClick={() => {
                                            const isAsc = orderBy === 'surname' && order === 'asc';
                                            setOrder(isAsc ? 'desc' : 'asc');
                                            setOrderBy('surname');
                                        }}
                                    >
                                        <strong>Surname</strong>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'first_given_name' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'first_given_name'}
                                        direction={orderBy === 'first_given_name' ? order : 'asc'}
                                        onClick={() => {
                                            const isAsc = orderBy === 'first_given_name' && order === 'asc';
                                            setOrder(isAsc ? 'desc' : 'asc');
                                            setOrderBy('first_given_name');
                                        }}
                                    >
                                        <strong>Firstname</strong>
                                    </TableSortLabel>
                                </TableCell>
                                {/* <TableCell sortDirection={orderBy === 'other_given_names' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'other_given_names'}
                                        direction={orderBy === 'other_given_names' ? order : 'asc'}
                                        onClick={() => {
                                            const isAsc = orderBy === 'other_given_names' && order === 'asc';
                                            setOrder(isAsc ? 'desc' : 'asc');
                                            setOrderBy('other_given_names');
                                        }}
                                    >
                                        <strong>Other given names</strong>
                                    </TableSortLabel>
                                </TableCell> */}
                                <TableCell sortDirection={orderBy === 'state' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'state'}
                                        direction={orderBy === 'state' ? order : 'asc'}
                                        onClick={() => {
                                            const isAsc = orderBy === 'state' && order === 'asc';
                                            setOrder(isAsc ? 'desc' : 'asc');
                                            setOrderBy('state');
                                        }}
                                    >
                                        <strong>State</strong>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'wwcc_number' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'wwcc_number'}
                                        direction={orderBy === 'wwcc_number' ? order : 'asc'}
                                        onClick={() => {
                                            const isAsc = orderBy === 'wwcc_number' && order === 'asc';
                                            setOrder(isAsc ? 'desc' : 'asc');
                                            setOrderBy('wwcc_number');
                                        }}
                                    >
                                        <strong>WWCC Number</strong>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'email' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'email'}
                                        direction={orderBy === 'email' ? order : 'asc'}
                                        onClick={() => {
                                            const isAsc = orderBy === 'email' && order === 'asc';
                                            setOrder(isAsc ? 'desc' : 'asc');
                                            setOrderBy('email');
                                        }}
                                    >
                                        <strong>Email</strong>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'expiry_date' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'expiry_date'}
                                        direction={orderBy === 'expiry_date' ? order : 'asc'}
                                        onClick={() => {
                                            const isAsc = orderBy === 'expiry_date' && order === 'asc';
                                            setOrder(isAsc ? 'desc' : 'asc');
                                            setOrderBy('expiry_date');
                                        }}
                                    >
                                        <strong>Expiry Date</strong>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'verified_status' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'verified_status'}
                                        direction={orderBy === 'verified_status' ? order : 'asc'}
                                        onClick={() => {
                                            setOrder(orderBy === 'verified_status' && order === 'asc' ? 'desc' : 'asc');
                                            setOrderBy('verified_status');
                                        }}
                                    >
                                        <strong>Verified Status</strong>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === 'days_left' ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === 'days_left'}
                                        direction={orderBy === 'days_left' ? order : 'asc'}
                                        onClick={() => {
                                            setOrder(orderBy === 'days_left' && order === 'asc' ? 'desc' : 'asc');
                                            setOrderBy('days_left');
                                        }}
                                    >
                                        <strong>Days Left</strong>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                [...Array(5)].map((_, index) => (
                                    <TableRow key={`skeleton-${index}`}>
                                        <TableCell><Skeleton variant="text" width={110} height={24} /></TableCell>      {/* 1. Surname (Text + space for optional badge) */}
                                        <TableCell><Skeleton variant="text" width={45} height={24} /></TableCell>       {/* 2. First Name */}
                                        {/* <TableCell><Skeleton variant="text" width="60%" /></TableCell> */}
                                        <TableCell><Skeleton variant="rectangular" width={45} height={24} sx={{ borderRadius: 1 }} /></TableCell>       {/* 3. State Chip (Matches standard outlined chip size) */}
                                        <TableCell><Skeleton variant="text" width={100} height={24} /></TableCell>      {/* 4. WWCC Number */}
                                        <TableCell><Skeleton variant="text" width={200} height={24} /></TableCell>      {/* 5. Email (Typically longer text string) */}
                                        <TableCell><Skeleton variant="text" width={90} height={24} /></TableCell>       {/* 6. Expiry Date */}
                                        <TableCell><Skeleton variant="rectangular" width={65} height={24} sx={{ borderRadius: 1 }} /></TableCell>       {/* 7. Status Chip (Matches standard filled chip size) */}
                                        <TableCell><Skeleton variant="text" width={60} height={24} /></TableCell>       {/* 8. Days Left */}
                                        <TableCell align="right">       {/* 9. Actions Column */}
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                <Skeleton variant="circular" width={34} height={34} />
                                                <Skeleton variant="circular" width={34} height={34} />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                sortedRecords.map((r) => {
                                    const today = new Date();
                                    const expiry = r.expiry_date ? parseISO(r.expiry_date) : null;

                                    // Calculate days left only if date is valid
                                    const daysLeft = (expiry && isValid(expiry))
                                        ? differenceInDays(expiry, today)
                                        : null;

                                    // --- EXTRACT TOOLTIP LOGIC ---
                                    // Read directly from the aliased string column populated by the API controller
                                    const rawApiResponseMsg = r.status_message || '';

                                    // Exclusively track the external registry audit timestamp
                                    const lastCheckedTimestamp = r.last_checked_at;
                                    const formattedTimestamp = lastCheckedTimestamp
                                        ? formatHumanReadableTimestamp(lastCheckedTimestamp)
                                        : 'Never Checked';

                                    const statusTooltipTitle = (
                                        <Box sx={{ p: 1.5 }}>
                                            <Typography variant="caption" component="div" sx={{ fontWeight: 'bold' }}>
                                                Last Checked: <span style={{ fontWeight: 'normal' }}>{formattedTimestamp}</span>
                                            </Typography>

                                            {/* Only include this segment in the DOM if the user is an admin/superuser */}
                                            {canEdit && rawApiResponseMsg && (
                                                <Typography
                                                    variant="caption"
                                                    component="div"
                                                    sx={{
                                                        maxWidth: 300,
                                                        whiteSpace: 'normal',
                                                        mt: 0.75,
                                                        pt: 0.75,
                                                        borderTop: '1px solid rgba(255,255,255,0.2)',
                                                        fontWeight: 'bold',
                                                        color: 'rgba(255,255,255,0.9)'
                                                    }}
                                                >
                                                    Last API Response: 
                                                    "<span style={{ fontStyle: 'italic', fontWeight: 'normal' }}>{rawApiResponseMsg}</span>"
                                                </Typography>
                                            )}
                                        </Box>
                                    );

                                    return (
                                        <TableRow
                                            key={r.id}
                                            sx={{
                                                bgcolor: r.is_active ? 'inherit' : 'action.hover',
                                                opacity: r.is_active ? 1 : 0.6
                                            }}
                                        >
                                            <TableCell>
                                                {r.surname}
                                                {!r.is_active && <Chip label="Inactive" size="small" sx={{ ml: 1 }} />}
                                            </TableCell>
                                            <TableCell>{r.first_given_name}</TableCell>
                                            {/* <TableCell>{r.other_given_names}</TableCell> */}
                                            <TableCell>
                                                <Chip label={r.au_state} size="small" variant="outlined" />
                                            </TableCell>
                                            <TableCell><code>{r.wwcc_number}</code></TableCell>
                                            <TableCell><code>{r.email}</code></TableCell>
                                            <TableCell
                                                sx={{
                                                    color: r.isExpired ? 'error.main' : 'inherit',
                                                    fontWeight: r.isExpired ? 500 : 400
                                                }}
                                            >
                                                {formatDate(r.expiry_date) || 'Unknown'}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={statusTooltipTitle} arrow placement="top">
                                                    <Box sx={{ display: 'inline-flex', gap: 1, cursor: 'help' }}>
                                                        {renderStatusChip(r)}
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {daysLeft !== null ? (
                                                    <Box sx={{
                                                        fontWeight: 'medium',
                                                        color: daysLeft <= 0 ? 'error.main' : (daysLeft <= 30 ? 'warning.main' : 'text.primary')
                                                    }}>
                                                        {daysLeft <= 0 ? 'Expired' : `${daysLeft} days`}
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ color: 'text.disabled' }}>—</Box>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">

                                                {/* NOTES INDICATOR TOOLTIP */}
                                                {r.notes && r.notes.trim() !== '' && (
                                                    <Tooltip
                                                        title={
                                                            <Box sx={{ p: 0.5, maxWidth: 250, whiteSpace: 'pre-wrap' }}>
                                                                <Typography variant="caption" component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                                    Internal Notes:
                                                                </Typography>
                                                                {r.notes}
                                                            </Box>
                                                        }
                                                        arrow
                                                        placement="top"
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            color="info"
                                                            sx={{
                                                                cursor: 'help',
                                                                opacity: 0.8,
                                                                '&:hover': { opacity: 1 }
                                                            }}
                                                        >
                                                            <ClipboardIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {/* EDIT & DELETE ACTION BUTTONS */}
                                                {r.is_active ? (
                                                    <>
                                                        <IconButton onClick={() => handleOpen(r)} color="primary" size="small"><EditIcon fontSize="small" /></IconButton>
                                                        <IconButton
                                                            onClick={() => handleDeleteClick(r)}
                                                            color="error"
                                                            size="small"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </>
                                                ) : (
                                                    canEdit && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="success"
                                                                startIcon={<RestoreIcon />}
                                                                onClick={() => handleReactivate(r)}
                                                            >
                                                                Reactivate
                                                            </Button>

                                                            {/* Hard Delete Icon for Superusers */}
                                                            {user.role === 'superuser' && (
                                                                <Tooltip title="Permanently Delete">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() => {
                                                                            setSelectedRecord(r);
                                                                            setHardDeleteConfirmOpen(true);
                                                                        }}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    )
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>

                    </Table>
                </TableContainer>
            </Paper>


            {/* CREATE/EDIT MODAL */}
            <WWCCDialog
                open={open}
                onClose={() => setOpen(false)}
                onSave={handleSave}
                record={selectedRecord}
                isEditing={isEditing}
            />



            {/* DELETE CONFIRMATION */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle>Delete WWCC Record?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete <strong>{selectedRecord?.first_given_name} {selectedRecord?.surname}</strong> ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>



            {/* HARD DELETE CONFIRMATION */}
            <Dialog open={hardDeleteConfirmOpen} onClose={() => setHardDeleteConfirmOpen(false)}>
                <DialogTitle sx={{ color: 'error.main' }}>Permanent Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to <strong>permanently delete</strong> "{selectedRecord?.first_given_name} {selectedRecord?.surname}"?
                        This action cannot be undone and will be removed from all logs.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setHardDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleHardDelete} color="error" variant="contained">
                        Delete Permanently
                    </Button>
                </DialogActions>
            </Dialog>

        </Box >
    );

}